import { useState, useMemo, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Search, Filter, AlertTriangle, CheckCircle2, Info, Boxes } from 'lucide-react'
import { useAllocation } from '../hooks/useAllocation.js'
import { trackEvent } from '../analytics/mixpanel.js'
import WeatherBadge from '../components/shared/WeatherBadge.jsx'
import SeverityBar from '../components/shared/SeverityBar.jsx'
import SignalBadge from '../components/shared/SignalBadge.jsx'
import LoadingSpinner, { SkeletonTable } from '../components/shared/LoadingSpinner.jsx'
import Tooltip from '../components/shared/Tooltip.jsx'
import StatStrip from '../components/shared/StatStrip.jsx'
import CollapsibleFilterBar from '../components/shared/CollapsibleFilterBar.jsx'
import FilterField from '../components/shared/FilterField.jsx'
import { DC_CODES, dcLabel } from '../utils/dc.js'

const DC_INFO = {
  HZL: 'Hazleton, PA — AEO DC #002959',
  MCI: 'Ottawa, KS — AEO DC #000659',
  CDC: 'Mississauga, ON — AEC DC #000978',
  LA_3PL: 'Los Angeles, CA — 3rd-party logistics',
}

const SEVERITY_TIPS = [
  'Level 0 — Clear: no weather disruption expected',
  'Level 1 — Minor: light conditions, minimal impact',
  'Level 2 — Moderate: some delays possible',
  'Level 3 — Significant: plan for disruption',
  'Level 4 — Severe: allocations at risk',
  'Level 5 — Extreme: major disruption, hold allocations',
]

const SIGNAL_TIPS = {
  STOP: 'Hold allocations — weather severity ≥ 4 AND sell-through sensitivity > 15%',
  GO: 'Proceed with normal allocation — risk thresholds not exceeded',
}

const PAGE_SIZE = 100

function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return <ChevronUp size={12} className="opacity-20" />
  return sortDir === 'asc'
    ? <ChevronUp size={12} className="text-cyan-400" />
    : <ChevronDown size={12} className="text-cyan-400" />
}

export default function AllocationTable() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [page, setPage] = useState(1)
  const [signal, setSignal] = useState('all')
  const [dc, setDc] = useState(() => searchParams.get('dc') ?? 'all')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [sortCol, setSortCol] = useState('forecast_severity')
  const [sortDir, setSortDir] = useState('desc')

  const { data, isLoading, isError, isFetching, refetch } = useAllocation({
    page,
    pageSize: PAGE_SIZE,
    signal,
    dc,
    search: debouncedSearch,
  })

  // Debounce search
  const handleSearch = (val) => {
    setSearch(val)
    clearTimeout(window.__searchTimer)
    window.__searchTimer = setTimeout(() => {
      setDebouncedSearch(val)
      setPage(1)
    }, 500)
  }

  // Fire Search Submitted exactly once per debounced query+result pair.
  const lastSearchTrackedRef = useRef(null)
  useEffect(() => {
    if (!debouncedSearch || isLoading || isFetching) return
    const key = `${debouncedSearch}|${data?.total ?? 'na'}`
    if (lastSearchTrackedRef.current === key) return
    lastSearchTrackedRef.current = key
    trackEvent('Search Submitted', {
      page: 'allocation',
      query_length: debouncedSearch.length,
      has_results: (data?.total ?? 0) > 0,
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, data?.total, isLoading, isFetching])

  const handleFilterChange = (setter, filterName) => (val) => {
    if (filterName) {
      trackEvent('Filter Changed', { page: 'allocation', filter_name: filterName, filter_value: val })
    }
    setter(val)
    setPage(1)
  }

  // Client-side sort of current page
  const sortedRows = useMemo(() => {
    const rows = data?.stores ?? []
    return [...rows].sort((a, b) => {
      const va = a[sortCol]
      const vb = b[sortCol]
      if (va === vb) return 0
      const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [data?.stores, sortCol, sortDir])

  const handleSort = (col) => {
    const nextDir = sortCol === col ? (sortDir === 'asc' ? 'desc' : 'asc') : 'desc'
    trackEvent('Sort Changed', { page: 'allocation', column: col, direction: nextDir })
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortCol(col)
      setSortDir('desc')
    }
  }

  const totalPages = data?.total_pages ?? 1

  const columns = [
    { key: 'store_id', label: 'Store ID', tip: 'Normalized AEO store number (leading zeros stripped)' },
    { key: 'city', label: 'Store', tip: 'Store city and state' },
    { key: 'primary_dc', label: 'DC', tip: 'Primary distribution center serving this store' },
    { key: 'forecast_category', label: 'Forecast', tip: 'Dominant weather category over the next 7 days' },
    { key: 'forecast_severity', label: 'Severity', tip: 'Max weather severity over 7 days — 0 (Clear) → 5 (Extreme)' },
    { key: 'sensitivity_score', label: 'Sensitivity', tip: 'Sell-through rate volatility (MAX–MIN across 12 weeks). Stores above 10% trigger STOP signals.' },
    { key: 'current_wos', label: 'WOS', tip: 'Weeks of Supply on hand at the current weekly sales rate' },
    { key: 'signal', label: 'Signal', tip: 'STOP = severity ≥ 3 AND sensitivity > 10%.  GO = safe to proceed.' },
  ]

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 xl:py-6 flex flex-col min-h-[calc(100vh-4rem)] xl:h-[calc(100vh-4rem)]">
      {/* ── Header ── */}
      <div className="mb-6">
        <motion.h1
          className="text-2xl font-black text-slate-100 flex items-center gap-2"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Boxes size={20} className="text-cyan-400" />
          Allocation Stop / Go
          <Tooltip content="Weather-driven allocation signals for all stores — click a row to view store detail" placement="bottom" asButton>
            <Info size={15} className="text-slate-500 hover:text-slate-300 cursor-help transition-colors" />
          </Tooltip>
        </motion.h1>
      </div>

      {/* ── Summary Stats ── */}
      {data && (
        <StatStrip
          items={[
            { label: 'Total stores', value: data.total.toLocaleString(), icon: Filter, tone: 'slate' },
            { label: 'STOP signals', value: data.stop_count.toLocaleString(), icon: AlertTriangle, tone: 'danger' },
            { label: 'GO signals', value: data.go_count.toLocaleString(), icon: CheckCircle2, tone: 'success' },
          ]}
        />
      )}

      {/* ── Filter Bar ── */}
      <CollapsibleFilterBar
        persistKey="aeo:filters:open:allocation"
        activeSummary={[
          dc !== 'all' && `DC: ${dc}`,
          signal !== 'all' && `Signal: ${signal}`,
          debouncedSearch && `Search: "${debouncedSearch}"`,
        ].filter(Boolean).join(' · ') || 'All stores'}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
          {/* Search */}
          <FilterField label="Search">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                placeholder="Search store ID..."
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-cyan-500/50 focus:bg-white/8 transition-colors"
              />
            </div>
          </FilterField>

          {/* DC filter */}
          <FilterField label="Distribution center">
            <select
              value={dc}
              onChange={(e) => handleFilterChange(setDc, 'dc')(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-slate-300 focus:outline-none focus:border-cyan-500/50 cursor-pointer"
            >
              {['all', ...DC_CODES].map((d) => (
                <option key={d} value={d}>{dcLabel(d)}</option>
              ))}
            </select>
          </FilterField>

          {/* Signal filter */}
          <FilterField label="Signal">
            <div className="flex rounded-lg overflow-hidden border border-white/10 w-fit">
              {['all', 'STOP', 'GO'].map((s) => (
                <button
                  key={s}
                  onClick={() => handleFilterChange(setSignal, 'signal')(s)}
                  className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide transition-colors ${
                    signal === s
                      ? s === 'STOP'
                        ? 'bg-red-500/30 text-red-300'
                        : s === 'GO'
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : 'bg-cyan-500/20 text-cyan-300'
                      : 'bg-transparent text-slate-500 hover:text-slate-300'
                  }`}
                >
                  {s === 'all' ? 'All' : s}
                </button>
              ))}
            </div>
          </FilterField>
        </div>

        {isFetching && (
          <div className="mt-3 text-xs text-slate-500 flex items-center gap-1">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="w-3 h-3 border border-cyan-400 border-t-transparent rounded-full"
            />
            Refreshing...
          </div>
        )}
      </CollapsibleFilterBar>

      {/* ── Table ── */}
      {isLoading ? (
        <div className="glass-card p-6">
          <SkeletonTable rows={10} />
        </div>
      ) : isError ? (
        <div className="glass-card p-8 text-center">
          <AlertTriangle size={40} className="text-amber-400 mx-auto mb-3" />
          <p className="text-slate-400 mb-4">Failed to load allocation data</p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 rounded-lg bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/30 transition-colors text-sm"
          >
            Retry
          </button>
        </div>
      ) : (
        <div className="glass-card overflow-hidden flex flex-col xl:flex-1 xl:min-h-0">
          <div className="overflow-auto min-h-[300px] max-h-[min(70vh,700px)] xl:flex-1 xl:min-h-0 xl:max-h-none">
            <table className="data-table w-full">
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      className="text-left whitespace-nowrap"
                    >
                      <Tooltip content={col.tip} placement="bottom">
                        <span className="inline-flex items-center gap-1 cursor-help border-b border-dashed border-slate-600">
                          {col.label}
                          <SortIcon col={col.key} sortCol={sortCol} sortDir={sortDir} />
                        </span>
                      </Tooltip>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {sortedRows.map((row, i) => (
                    <motion.tr
                      key={row.store_id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ delay: Math.min(i * 0.015, 0.3) }}
                      onClick={() => {
                        trackEvent('Store Drilldown', { store_id: row.store_id, source: 'allocation', signal: row.signal })
                        navigate(`/stores/${row.store_id}`)
                      }}
                      className="cursor-pointer"
                      title={row.signal_reason}
                    >
                      <td>
                        <span className="font-mono font-bold text-cyan-400">{row.store_id}</span>
                      </td>
                      <td>
                        <span className="text-sm text-slate-300">
                          {row.city && row.state ? `${row.city}, ${row.state}` : row.city || row.state || '—'}
                        </span>
                      </td>
                      <td>
                        <Tooltip content={DC_INFO[row.primary_dc] ?? row.primary_dc} placement="right">
                          <span className="inline-block px-2 py-0.5 rounded text-xs font-bold bg-navy-600/60 text-slate-300 border border-white/10 cursor-help">
                            {row.primary_dc}
                          </span>
                        </Tooltip>
                      </td>
                      <td>
                        <WeatherBadge category={row.forecast_category} />
                      </td>
                      <td>
                        <Tooltip content={SEVERITY_TIPS[Math.min(5, Math.max(0, Math.round(row.forecast_severity ?? 0)))]} placement="top">
                          <span className="cursor-help">
                            <SeverityBar severity={row.forecast_severity} showLabel={false} />
                          </span>
                        </Tooltip>
                      </td>
                      <td>
                        <Tooltip
                          content={
                            row.sensitivity_score > 0.15
                              ? 'High volatility — above 15% threshold, triggers STOP signal'
                              : 'Normal volatility — below 15% STOP threshold'
                          }
                          placement="top"
                        >
                          <span className={`text-sm font-semibold cursor-help ${
                            row.sensitivity_score > 0.15 ? 'text-amber-400' : 'text-slate-300'
                          }`}>
                            {(row.sensitivity_score * 100).toFixed(1)}%
                          </span>
                        </Tooltip>
                      </td>
                      <td>
                        <Tooltip
                          content={
                            row.current_wos < 4
                              ? 'Critical — less than 4 weeks of inventory on hand'
                              : row.current_wos < 8
                              ? 'Low — monitor closely, replenishment may be needed soon'
                              : 'Healthy stock level'
                          }
                          placement="top"
                        >
                          <span className={`text-sm font-semibold cursor-help ${
                            row.current_wos < 4 ? 'text-red-400' :
                            row.current_wos < 8 ? 'text-amber-400' :
                            'text-emerald-400'
                          }`}>
                            {row.current_wos.toFixed(1)}w
                          </span>
                        </Tooltip>
                      </td>
                      <td>
                        <Tooltip content={SIGNAL_TIPS[row.signal] ?? ''} placement="left">
                          <span className="cursor-help">
                            <SignalBadge signal={row.signal} />
                          </span>
                        </Tooltip>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>

            {sortedRows.length === 0 && (
              <div className="p-12 text-center text-slate-500">
                No stores match your filters
              </div>
            )}
          </div>

          {/* ── Pagination ── */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-white/5">
              <span className="text-xs text-slate-500">
                Page {data?.page} of {totalPages} • {data?.total.toLocaleString()} stores
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const next = Math.max(1, page - 1)
                    trackEvent('Pagination Changed', { page: 'allocation', to_page: next, total_pages: totalPages })
                    setPage(next)
                  }}
                  disabled={page === 1}
                  className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-slate-400 hover:text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={14} />
                </button>
                {/* Page numbers — hidden below md per responsive standard */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const p = Math.max(1, Math.min(totalPages - 4, page - 2)) + i
                  return (
                    <button
                      key={p}
                      onClick={() => {
                        trackEvent('Pagination Changed', { page: 'allocation', to_page: p, total_pages: totalPages })
                        setPage(p)
                      }}
                      className={`hidden md:block px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                        p === page
                          ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                          : 'bg-white/5 border border-white/10 text-slate-400 hover:text-slate-200'
                      }`}
                    >
                      {p}
                    </button>
                  )
                })}
                <button
                  onClick={() => {
                    const next = Math.min(totalPages, page + 1)
                    trackEvent('Pagination Changed', { page: 'allocation', to_page: next, total_pages: totalPages })
                    setPage(next)
                  }}
                  disabled={page === totalPages}
                  className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-slate-400 hover:text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
